import { format } from "date-fns";

import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import type { LoggedInUser, UsersOrderData } from "@/types";

interface AnalyticsTabProps {
	ordersData: UsersOrderData[];
	usersData: LoggedInUser[];
	isLoading: boolean;
	searchQuery: string;
}

const AnalyticsTab = ({
	ordersData,
	usersData,
	searchQuery,
}: AnalyticsTabProps) => {
	const filteredOrders = ordersData.filter((order) => {
		if (!searchQuery) return true;
		const searchLower = searchQuery.toLowerCase();
		return (
			order.user.fullName?.toLowerCase().includes(searchLower) ||
			format(new Date(order.collectionDate), "PPP")
				.toLowerCase()
				.includes(searchLower) ||
			order.totalPrice.toString().includes(searchLower)
		);
	});

	const getMonthlyRevenue = () => {
		const monthlyRevenue: { [key: string]: number } = {};
		for (const order of filteredOrders) {
			if (!order.cancelled) {
				const month = format(new Date(order.createdAt), "MMM yyyy");
				monthlyRevenue[month] = (monthlyRevenue[month] || 0) + order.totalPrice;
			}
		}
		return monthlyRevenue;
	};

	const getOrderStatusCounts = () => {
		return {
			confirmed: filteredOrders.filter(
				(order) => order.confirmed && !order.collected && !order.cancelled,
			).length,
			collected: filteredOrders.filter(
				(order) => order.collected && !order.delivered && !order.cancelled,
			).length,
			delivered: filteredOrders.filter((order) => order.delivered).length,
			cancelled: filteredOrders.filter((order) => order.cancelled).length,
		};
	};

	const getAverageOrderValue = () => {
		const validOrders = filteredOrders.filter((order) => !order.cancelled);
		if (validOrders.length === 0) return 0;
		const total = validOrders.reduce((sum, order) => sum + order.totalPrice, 0);
		return total / validOrders.length;
	};

	const monthlyRevenue = getMonthlyRevenue();
	const statusCounts = getOrderStatusCounts();
	const averageOrderValue = getAverageOrderValue();

	return (
		<div className="grid gap-4">
			<Card>
				<CardHeader>
					<CardTitle>Analytics</CardTitle>
					<CardDescription>
						{searchQuery
							? `Showing analytics for orders matching "${searchQuery}"`
							: "Business performance metrics and trends"}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						{Object.entries(monthlyRevenue).map(([month, revenue]) => (
							<div
								key={month}
								className="flex items-center justify-between rounded-lg border p-4"
							>
								<p className="font-medium">{month}</p>
								<p className="text-lg font-semibold">£{revenue.toFixed(2)}</p>
							</div>
						))}
					</div>
				</CardContent>
			</Card>

			<div className="grid gap-4 md:grid-cols-2">
				<Card>
					<CardHeader>
						<CardTitle>Order Status</CardTitle>
						<CardDescription>Current order distribution</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="space-y-2">
							<div className="flex items-center justify-between">
								<p>Ready for Collection</p>
								<p className="font-medium">{statusCounts.confirmed}</p>
							</div>
							<div className="flex items-center justify-between">
								<p>Ready for Delivery</p>
								<p className="font-medium">{statusCounts.collected}</p>
							</div>
							<div className="flex items-center justify-between">
								<p>Completed</p>
								<p className="font-medium">{statusCounts.delivered}</p>
							</div>
							<div className="flex items-center justify-between">
								<p>Cancelled</p>
								<p className="font-medium">{statusCounts.cancelled}</p>
							</div>
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>Performance Metrics</CardTitle>
						<CardDescription>Key business metrics</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="space-y-4">
							<div className="flex items-center justify-between">
								<p>Total Orders</p>
								<p className="font-medium">{filteredOrders.length}</p>
							</div>
							<div className="flex items-center justify-between">
								<p>Total Customers</p>
								<p className="font-medium">{usersData.length}</p>
							</div>
							<div className="flex items-center justify-between">
								<p>Average Order Value</p>
								<p className="font-medium">£{averageOrderValue.toFixed(2)}</p>
							</div>
							<div className="flex items-center justify-between">
								<p>Total Revenue</p>
								<p className="font-medium">
									£
									{filteredOrders
										.filter((order) => !order.cancelled)
										.reduce((sum, order) => sum + order.totalPrice, 0)
										.toFixed(2)}
								</p>
							</div>
						</div>
					</CardContent>
				</Card>
			</div>
		</div>
	);
};

export default AnalyticsTab;
